import { Button, ButtonTheme } from 'components/Button';
import { PageToolbar } from 'components/PageToolbar';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { ModalScreens } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { filterArrayEmpty, randomKey } from 'lib/riskbase';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  doOpenPropertySettingsModal,
  doEngageFlatsLoad,
  doEngageFlatsSetFilter,
} from 'store/remote/engageActions';
import {
  selectEngageFlatsFilterSearch,
  selectEngageFlatsFilterOccupationStatuses,
  selectEngageFlatsFilterIssueContactTypes,
  selectEngageFlatsFilterIsCommerciallyOwned,
  selectEngageFlatsFilterIsHMO,
  selectEngageFlatsRequiresResidentSupport,
  selectEngageIsLoading,
  selectEngageIsLoadedEver,
} from 'store/remote/engageSelectors';
import { EngageFlatsFilter, EngagePropertyListItem } from 'types/Engage';
import { Highlight } from 'components/Highlight';
import { BlockManagerPropertyFeatureEnums } from 'types/DTOs/BlockManagerDTOs';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';
import { PageHeader } from 'components/PageHeader';
import { ButtonGroup } from 'components/ButtonGroup';

type Props = {
  property: EngagePropertyListItem;
  onBackPress: () => void;
  onReloadPress: () => void;
  onOnboardFlatsPress: () => void;
  onExportCsvPress: () => void;
  onScheduledVisitsPress: () => void;
  flatsCount: number;
  pageKey: string;
  filtersLocked?: boolean;
  hasBulkSelection?: boolean;
  onBulkSendPress?: () => void;
};

export const EngageFlatsHeader: React.FC<Props> = ({
  property,
  onBackPress,
  onReloadPress,
  onOnboardFlatsPress,
  onExportCsvPress,
  onScheduledVisitsPress,
  flatsCount,
  pageKey,
  filtersLocked = false,
  hasBulkSelection = false,
  onBulkSendPress,
}) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const isLoadedEver = useSelector((state) =>
    selectEngageIsLoadedEver(state, pageKey, 'flats'),
  );
  const isLoading = useSelector((state) =>
    selectEngageIsLoading(state, pageKey, 'flats'),
  );
  const searchValue = useSelector((state) =>
    selectEngageFlatsFilterSearch(state, pageKey),
  );
  const selectedOccupationStatuses = useSelector((state) =>
    selectEngageFlatsFilterOccupationStatuses(state, pageKey),
  );
  const contactIssueTypeStatus = useSelector((state) =>
    selectEngageFlatsFilterIssueContactTypes(state, pageKey),
  );
  const requiresResidentSupportStatus = useSelector((state) =>
    selectEngageFlatsRequiresResidentSupport(state, pageKey),
  );
  const isHMOFilterStatus = useSelector((state) =>
    selectEngageFlatsFilterIsHMO(state, pageKey),
  );
  const isCommerciallyOwnedFilterStatus = useSelector((state) =>
    selectEngageFlatsFilterIsCommerciallyOwned(state, pageKey),
  );
  const propertyFeatures = property.features ?? [];
  const hasScheduledVisits = propertyFeatures.includes(
    BlockManagerPropertyFeatureEnums.scheduledvisits,
  );
  const hasImportFlatContacts = propertyFeatures.includes(
    BlockManagerPropertyFeatureEnums.importflatcontacts,
  );

  const searchValueRef = React.useRef(searchValue);
  const textfieldKeyRef = React.useRef(randomKey());
  if (searchValueRef.current !== searchValue) {
    textfieldKeyRef.current = randomKey();
    searchValueRef.current = searchValue;
  }

  const handleEngageFilter = React.useCallback(
    (filterChanges: Partial<EngageFlatsFilter>) => {
      if (filtersLocked) {
        return;
      }
      dispatch(
        doEngageFlatsSetFilter({
          key: pageKey,
          data: filterChanges,
        }),
      );
      dispatch(
        doEngageFlatsLoad({
          key: pageKey,
          propertyId: property.id,
          page: 'reset',
        }),
      );
    },
    [dispatch, filtersLocked, pageKey, property.id],
  );

  const handleSearch = React.useCallback(
    (newSearchValue: string) => {
      searchValueRef.current = newSearchValue;
      handleEngageFilter({ search: newSearchValue });
    },
    [handleEngageFilter],
  );

  const filterIsTriggered =
    (searchValue?.trim()?.length ?? 0) > 0 ||
    (selectedOccupationStatuses?.length ?? 0) > 0 ||
    (contactIssueTypeStatus?.length ?? 0) > 0 ||
    (requiresResidentSupportStatus?.length ?? 0) > 0 ||
    (isHMOFilterStatus?.length ?? 0) > 0 ||
    (isCommerciallyOwnedFilterStatus?.length ?? 0) > 0;

  const handleOpenFiltersModal = React.useCallback(() => {
    if (filtersLocked) {
      return;
    }
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageFlatsFilter,
        size: 'small',
        params: {
          pageKey,
          propertyId: property.id,
        },
      }),
    );
  }, [dispatch, filtersLocked, pageKey, property.id]);

  const handleOpenPropertySettingsModal = React.useCallback(() => {
    dispatch(doOpenPropertySettingsModal({ propertyId: property.id, pageKey }));
  }, [dispatch, property.id, pageKey]);

  return (
    <View>
      <PageHeader
        alignContent="left"
        showSidebarButton
        title={property.name}
        subtitle={
          !isLoadedEver
            ? 'Loading Flats'
            : pluralise(flatsCount, '{x} Flat', '{x} Flats', 'No Flats')
        }
        backButtonText="Engage"
        onPressBackButton={onBackPress}
        buttons={
          hasScheduledVisits &&
          screenSize === ScreenSize.large && (
            <Button
              text="Scheduled Visits"
              onPress={onScheduledVisitsPress}
              buttonTheme={ButtonTheme.themeless}
            />
          )
        }
        menuRight={{
          iconButtonConfig: { icon: ICONS.EllipsisLight },
          disabled: isLoading,
          options: [
            hasScheduledVisits && screenSize !== ScreenSize.large
              ? {
                  text: 'Scheduled Visits',
                  subText: 'Manage scheduled visits',
                  onPress: onScheduledVisitsPress,
                }
              : null,
            '-',
            hasImportFlatContacts
              ? {
                  text: 'Import Contacts',
                  subText: 'Upload a CSV of contacts',
                  icon: ICONS.FileImportLight,
                  onPress: onExportCsvPress,
                }
              : null,
            '-',
            {
              text: 'Property Settings',
              subText: 'Manage property settings',
              onPress: handleOpenPropertySettingsModal,
            },
            {
              text: 'Onboard Flats',
              subText: 'Onboard new flats into engage',
              onPress: onOnboardFlatsPress,
            },
          ].filter(filterArrayEmpty),
        }}
      />
      <PageToolbar
        key={textfieldKeyRef.current}
        searchValue={searchValue}
        onSearch={!filtersLocked && handleSearch}
        searchPlaceholder="Search flats and contacts..."
        searchDisabled={filtersLocked || isLoading}
        searchOnSubmit
        leftChildren={
          filtersLocked && hasBulkSelection && onBulkSendPress ? (
            <View style={{ flex: 1 }}>
              <View style={{ alignItems: 'flex-start' }}>
                <Highlight highlight style={{ borderRadius: 99 }}>
                  <Button
                    icon={ICONS.PaperPlaneLight}
                    text="Send Message"
                    onPress={onBulkSendPress}
                  />
                </Highlight>
              </View>
            </View>
          ) : undefined
        }
      >
        <ButtonGroup>
          <IconButton
            icon={ICONS.RotateRightLight}
            onPress={onReloadPress}
            disabled={filtersLocked || isLoading}
          />
          <IconButton
            icon={ICONS.SlidersUpLight}
            onPress={handleOpenFiltersModal}
            showBadge={filterIsTriggered}
            disabled={filtersLocked || isLoading}
          />
        </ButtonGroup>
      </PageToolbar>
    </View>
  );
};
